<template>
	<article class="my-workflow" @click="goToWorkflow">
		<div class="my-workflow_meta subheading-1 mobile-caption-2">
			<header class="my-workflow_head" >
				<a
					class="my-workflow_title my-workflow_title-link"
					@mouseover="showTooltip"
					@mouseleave="closeTooltip"
				>
					{{ title }}
				</a>
			</header>
			<transition name="title_tooltip">
				<span
					v-if="tooltipInfo"
					class="title_tooltip wf-tooltip wf-tooltip-bottom"
					:style="{top: tooltipInfo.top, left: tooltipInfo.left}"
				>
					{{ tooltipInfo.title }}
				</span>
			</transition>
			<aside>
				<slot name="aside"></slot>
			</aside>
		</div>
		<section class="my-workflow_steps">
			<div class="scrolling-wrapper" ref="scrollingWrapper" data-testid="scrolling-wrapper">
				<ul class="steps-list">
					<li v-for="step in steps" class="step-item">
						<span class="steps-list_step caption">{{ step }}</span>
						<div class="steps-list_icon">
							<arrow-forward />
						</div>
					</li>
				</ul>
			</div>
		</section>
	</article>
</template>

<script>
import ArrowForward from '@/components/icons/ArrowForward';
export default {
	components: {
		ArrowForward
	},
	props: {
		workflow: {type: Object},
	},
	data() {
		return {
			tooltipInfo: null,
		};
	},
	computed: {
		title() {
			return this.workflow?.title || '';
		},
		steps() {
			return this.workflow?.steps || [];
		},
		workflowUrl() {
			return `/workflow-configurator/workflows/${this.workflow.url}`
		}
	},
	methods: {
		showTooltip(ctx) {
			const link = ctx.target;
			this.tooltipInfo = {
				title: link.innerText,
				left: `${link.offsetLeft}px`,
				top: `${link.offsetHeight + link.offsetTop}px`,
			};
		},
		closeTooltip() {
			this.tooltipInfo = null;
		},

		handleScrollListener() {
			const slider = this.$refs.scrollingWrapper;
			let isDown = false;
			let startX;
			let scrollLeft;

			slider.addEventListener('mousedown', (e) => {
				isDown = true;
				slider.classList.add('active');
				startX = e.pageX - slider.offsetLeft;
				scrollLeft = slider.scrollLeft;
			});
			slider.addEventListener('mouseleave', () => {
				isDown = false;
				slider.classList.remove('active');
			});
			slider.addEventListener('mouseup', () => {
				isDown = false;
				slider.classList.remove('active');
			});
			slider.addEventListener('mousemove', (e) => {
				if (!isDown) return;
				e.preventDefault();
				const x = e.pageX - slider.offsetLeft;
				const walk = (x - startX) * 3; // scroll-fast
				slider.scrollLeft = scrollLeft - walk;
			});
		},
		goToWorkflow() {
			this.$emit('workflow-click', this.workflowUrl)
		},
	},
	mounted() {
		this.handleScrollListener();
	},
};
</script>

<style lang="scss" scoped>
$workflow-indent-side: 16px;

.my-workflow {
	@include font-styles('Futura W01 Book', 400);
	background: $white;
	border-radius: 4px;
	margin-bottom: 28px;
	transition: box-shadow 350ms ease-in;
	@media (min-width: $tablet) {
		margin-bottom: 16px;
	}
	&:hover {
		box-shadow: 0 4px 8px rgba(8, 35, 48, 0.24);
	}
	cursor: pointer;
}
.my-workflow_meta {
	position: relative;
	display: flex;
	padding: 10px 0 13px $workflow-indent-side;
	border-bottom: 1px solid $color-light-grey;
	@media (min-width: $tablet) {
		align-items: center;
		padding: 7px 0 9px $workflow-indent-side;
	}
}
.my-workflow_mark {
	font-size: 13px;
	color: $color-blue;
	background: $color-extra-light-blue;
	letter-spacing: 0.4px;
	padding: 4px 8px;
	margin-right: 16px;
	border-radius: 4px;
}
.my-workflow_head {
	overflow: hidden;
	display: flex;

	flex: 1;
	@media (max-width: $tablet) {
		padding-right: 45px;
		justify-content: space-between;
	}
}
.my-workflow_title {
	overflow: hidden;
	padding-right: 7%;
	&:hover {
		+ .title_tooltip {
			opacity: 1;
			transform: scale(0.95);
		}
	}
}
.my-workflow_title-link {
	overflow: hidden;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	@media (min-width: $tablet) {
		-webkit-line-clamp: 1;
	}
}
.title_tooltip {
	@include font-styles('Futura W01 Book', 400);
	@include textStyles(13, 20, 0.4);
	background-color: $color-tooltip-grey;
	color: $white;
	white-space: normal;
	padding: 0 8px;
	position: absolute;
	top: 100%;
	left: 0;
	z-index: 111;
	border-radius: 2px;
	transition: 0.15s cubic-bezier(0, 0, 0.2, 1);
	transition-property: opacity, transform;
	max-width: 75%;
	text-overflow: ellipsis;
	overflow: hidden;
	@media (min-width: $tablet) {
		max-width: 751px;
	}
}
.title_tooltip-leave-active {
	transition-timing-function: cubic-bezier(0.4, 0, 1, 1);
}

.title_tooltip-enter,
.title_tooltip-leave-active {
	opacity: 0;
	transform: scale(0);
	.wf-tooltip-top {
		transform: translate3d(0, 4px, 0) scale(0.95);
	}

	.wf-tooltip-right {
		transform: translate3d(-4px, 0, 0) scale(0.95);
	}

	.wf-tooltip-bottom {
		transform: translate3d(0, -4px, 0) scale(0.95);
	}

	.wf-tooltip-left {
		transform: translate3d(4px, 0, 0) scale(0.95);
	}
}

.my-workflow_steps {
	position: relative;
	padding: 12px 0 $workflow-indent-side/2;
	@media (min-width: $tablet) {
		padding: $workflow-indent-side 0;
	}
}
.scrolling-wrapper {
	user-select: none;
	
	overflow: -moz-scrollbars-none;
	-ms-overflow-style: none;
	-webkit-overflow-scrolling: touch;
	scrollbar-width: none;
	display: flex;
	align-items: center;
	overflow: hidden;
	white-space: nowrap;

	&:hover {
		overflow: scroll;
	}
	&:active {
		cursor: grabbing;
	}
	&::-webkit-scrollbar {
		width: 0 !important;
		display: none;
	}
	&:before {
		content: '';
		position: absolute;
		top: 0;
		bottom: 0;
		z-index: 10;
		width: 20px;
		left: 0;
		background: linear-gradient(90deg, #fff 25%, rgba(#fff, 0.834254) 50%, rgba(#fff, 0) 100%);
	}
	&:after {
		content: '';
		position: absolute;
		top: 0;
		bottom: 0;
		z-index: 10;
		width: 20px;
		right: 0;
		background: linear-gradient(270deg, #fff 25%, rgba(#fff, 0.834254) 50%, rgba(#fff, 0) 100%);
	}
}

.steps-list {
	display: flex;
	margin: 0;
	padding: 0;
	list-style: none;
	li {
		display: flex;
		align-items: center;
		&:first-child {
			margin-left: $workflow-indent-side;
		}
		&:last-child {
			margin-right: $workflow-indent-side;
		}
	}
}
.steps-list_step {
	background: $color-light-grey;
	border-radius: 8px;
	padding: 5px 16px;
}
.steps-list_icon {
	color: $color-mid-grey;
	margin: 0 20px;
}
.step-item:last-child {
	.steps-list_icon {
		display: none;
	}
}
</style>
